import { Component, ElementRef, EventEmitter, HostBinding, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NotificationService } from '../../core/services';
import { GenericService } from '../../core/services/GenericService';
import { BaseServiceErrorResolverService } from '../../core/services/error-resolvers/base-service-error-resolver.service';
import { environment } from '../../../environments/environment';
import { ISimpleListItem } from '../../core/interfaces/ISimpleListItem';
import {
  invalidEmailMessage,
  markTreeDirty,
  markTreePristine,
  requiredMessage,
  scrollFirstInvalid,
} from '../../core/utils';

@Component({
    template: '',
    standalone: false
})
export abstract class AddEditFormGeneric<T> implements OnInit {

  @Input() set editObject(val: T | null) {
    this._editObject = val;
    this.loadForm();
  }

  @Output() formFinished = new EventEmitter<boolean>();

  dateFormat = environment.defaultDateFormat;
  form: FormGroup;
  isSubmitted = false;
  isEdit = false;
  public loading = false;
  public readonly requiredMsg = requiredMessage;
  public readonly invalidEmailMsg = invalidEmailMessage;
  protected _editObject: T | null = null;
  private readonly _host = inject(ElementRef<HTMLElement>);
  private _initialSnapshot: unknown;

  @HostBinding('class.form-awaiting-submit')
  get awaitingSubmit(): boolean {
    return !this.isSubmitted;
  }

  protected constructor(
    protected _fb: FormBuilder,
    protected _formService: GenericService<T>,
    protected _notifyService: NotificationService,
    protected _errorResolver: BaseServiceErrorResolverService
  ) {

  }

  ngOnInit(): void {
    this.captureInitialSnapshot();
  }

  /** Hide error chrome until Speichern. Re-open starts clean. */
  resetSubmitState(resetValues = false): void {
    this.isSubmitted = false;
    if (!this.form) {
      return;
    }
    this.captureInitialSnapshot();
    if (resetValues && this._initialSnapshot != null) {
      this.form.reset(this._initialSnapshot as never);
    }
    markTreePristine(this.form);
  }

  get editObject(): T | null {
    return this._editObject;
  }

  loadForm(): void {
    if (this.editObject) {
      this.loading = true;
      this.isEdit = true;
      this._formService.getById(this.editObject['id']).subscribe(value => {
        this._editObject = value;
        this.beforeLoadForm();
        this.patchForm();
        this.afterLoadForm();
        this.resetSubmitState(false);
        this.loading = false;
      });
    }
  }

  submitForm(): void {
    this.isSubmitted = true;
    markTreeDirty(this.form);

    if (!this.form.valid) {
      this.formFinished.emit(false);
      setTimeout(() => scrollFirstInvalid(this._host.nativeElement));
      return;
    }

    if (this.editObject !== null) {
      this.saveEdit();
      return;
    }

    this.saveNew();
  }

  saveEdit(): void {
    const object: T = {...this.editObject, ...this.form.value};
    this._formService.update(object, this.editObject['id']).subscribe(() => {
        this._notifyService.pushSuccess('Erfolgreich', `Element wurde korrekt aktualisiert.`);
        this.formFinished.emit(true);
      }, err => {
        this.parseError(err);
        this.formFinished.emit(false);
      });
  }

  saveNew(): void {
    const object: T = this.form.value;
    this._formService.create(object).subscribe(() => {
        this._notifyService.pushSuccess('Erfolgreich', `Element wurde korrekt erstellt`);
        this.formFinished.emit(true);
      }, err => {
        this.parseError(err);
        this.formFinished.emit(false);
      });
  }

  parseError = data => {
    this._notifyService.pushError(undefined, this._errorResolver.getMessage(
      (data && data.InternalCode) ? data.InternalCode : '',
      data?.Message || data?.message
    ));
  }

  compareSimpleList(val1: ISimpleListItem, val2: ISimpleListItem): boolean {
    return val1?.id?.toString() === val2?.id?.toString();
  }

  beforeLoadForm() {}

  protected patchForm(): void {
    this.form.patchValue(this.editObject);
  }

  protected afterLoadForm(): void {}

  private captureInitialSnapshot(): void {
    if (this.form && this._initialSnapshot == null) {
      this._initialSnapshot = this.form.getRawValue();
    }
  }
}
